import React, { useState, useEffect, useCallback, useRef } from 'react';
import { T, API_BASE } from '../constants';

// JUYI Monitor — RTH 3m confluence reversal (LONG + SHORT arms). Polls
// /strategies/api/strategies/juyi/decisions/ every 20s. Thin Pine emitter posts
// triggers; backend runs regime gate (lag-1) + confluence + ANYANWU interaction
// sizing. Entry = trigger bar open, exit 16:00. SEPARATE book (never nets with
// ANYANWU). certs LONG C6135B4B / SHORT 02191BEA.

const REFRESH_MS = 20000;
const JUYI_COLOR = '#a855f7';   // violet (confluence)

const dirColor = (d) => (d === 'LONG' ? T.bull : d === 'SHORT' ? T.bear : T.textMuted);
const STATUS_COLORS = { OPEN: '#3b82f6', EXIT: '#3b82f6', TAKE: JUYI_COLOR, SKIP: T.textMuted, PAUSED: T.textMuted };
const num = (v, d = 2) => (v == null ? '—' : Number(v).toFixed(d));

function fmtET(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', { timeZone: 'America/New_York', hour12: false,
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function Stat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 100px', padding: '12px 14px', borderRadius: 6,
      background: 'rgba(255,255,255,0.02)', border: `1px solid ${T.border}` }}>
      <div style={{ fontSize: 10, color: T.textMuted, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 22, color: color || T.text, fontFamily: 'Roboto Mono, monospace', marginTop: 4 }}>{value}</div>
    </div>
  );
}
function Pill({ value, color }) {
  if (value == null || value === '') return <span style={{ color: T.textMuted }}>—</span>;
  return (
    <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10,
      fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: `${color || T.border}22`, border: `1px solid ${color || T.border}66`, color: color || T.text }}>{String(value)}</span>
  );
}

export default function JuyiMonitor() {
  const [data, setData] = useState({ decisions: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [actionFilter, setActionFilter] = useState('');
  const [dirFilter, setDirFilter] = useState('');
  const [limit, setLimit] = useState(100);
  const timerRef = useRef(null);

  const fetchRows = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let url = `${API_BASE}/strategies/api/strategies/juyi/decisions/?limit=${limit}`;
      if (actionFilter) url += `&action=${actionFilter}`;
      if (dirFilter) url += `&dir=${dirFilter}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setData({ decisions: j.decisions || [], summary: j.summary || {} });
      setLastFetched(new Date());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [limit, actionFilter, dirFilter]);

  useEffect(() => {
    fetchRows();
    timerRef.current = setInterval(fetchRows, REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [fetchRows]);

  const { decisions, summary } = data;

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1600, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 4 }}>
        <h1 style={{ margin: 0, fontSize: 22, color: T.text }}>JUYI — RTH Confluence Reversal (NQ)</h1>
        <Pill value="PHASE 1" color={JUYI_COLOR} />
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16, lineHeight: 1.5 }}>
        NQ 3m RTH · LONG (moderate uptrend 0–800bp + pullback, 4+ algos) · SHORT (extended &gt;800bp, 5+ algos) ·
        entry = trigger-bar open · exit 16:00 · ANYANWU-interaction sizing (separate book, never nets) ·
        certs LONG …C6135B4B / SHORT …02191BEA · LONG 86t/70.9% · SHORT 54t/79.6% · combined +38.8% / DD −1.30%
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Signals" value={summary.signals ?? 0} />
        <Stat label="Takes" value={summary.takes ?? 0} color={JUYI_COLOR} />
        <Stat label="Longs" value={summary.longs ?? 0} color={T.bull} />
        <Stat label="Shorts" value={summary.shorts ?? 0} color={T.bear} />
        <Stat label="Open" value={summary.open ?? 0} color="#3b82f6" />
        <Stat label="Net bp" value={num(summary.net_bp, 1)} color={(summary.net_bp || 0) >= 0 ? T.bull : T.bear} />
      </div>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
          style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px' }}>
          <option value="">All actions</option><option value="TAKE">TAKE</option><option value="SKIP">SKIP</option>
        </select>
        <select value={dirFilter} onChange={e => setDirFilter(e.target.value)}
          style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px' }}>
          <option value="">Both dirs</option><option value="LONG">LONG</option><option value="SHORT">SHORT</option>
        </select>
        <select value={limit} onChange={e => setLimit(Number(e.target.value))}
          style={{ background: T.surface, color: T.text, border: `1px solid ${T.border}`, borderRadius: 6, padding: '6px 10px' }}>
          {[50, 100, 200].map(n => <option key={n} value={n}>{n} rows</option>)}
        </select>
        <button onClick={fetchRows} disabled={loading}
          style={{ background: JUYI_COLOR, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, cursor: 'pointer' }}>
          {loading ? '…' : 'Refresh'}</button>
        <span style={{ fontSize: 11, color: T.textMuted }}>
          {decisions.length} rows · newest first {lastFetched ? `· updated ${lastFetched.toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false })} ET` : ''}
        </span>
      </div>

      {error && <div style={{ color: T.bear, marginBottom: 12 }}>Error: {error}</div>}

      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '60vh', border: `1px solid ${T.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: 'rgba(255,255,255,0.03)', textAlign: 'left' }}>
              {['Time (ET)', 'Dir', 'Trigger', 'Conf', 'Action', 'Size', 'pct_sma', 'r1', 'pos_rng', 'ANYANWU', 'Interaction', 'Px', 'PnL bp', 'Status', 'Reason'].map(h => (
                <th key={h} style={{ padding: '10px 12px', color: T.textMuted, fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {decisions.length === 0 && (
              <tr><td colSpan={15} style={{ padding: 24, textAlign: 'center', color: T.textMuted }}>No signals yet.</td></tr>
            )}
            {decisions.map(r => (
              <tr key={r.id} style={{ borderTop: `1px solid ${T.border}` }}>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', whiteSpace: 'nowrap' }}>{fmtET(r.signal_time)}</td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.direction} color={dirColor(r.direction)} /></td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{r.trigger || '—'}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{r.n_conf ?? '—'}</td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.action} color={r.action === 'TAKE' ? JUYI_COLOR : T.textMuted} /></td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{r.size ? `${num(r.size, 1)}×` : '—'}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{num(r.pct_sma_lag, 0)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{num(r.r1_lag, 0)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{num(r.pos_in_range, 2)}</td>
                <td style={{ padding: '9px 12px' }}>{r.anyanwu_dir && r.anyanwu_dir !== 'none' ? <Pill value={r.anyanwu_dir} color={dirColor(r.anyanwu_dir)} /> : '—'}</td>
                <td style={{ padding: '9px 12px', color: T.textMuted, whiteSpace: 'nowrap' }}>{r.interaction || '—'}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace' }}>{num(r.px)}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'Roboto Mono, monospace', color: (r.pnl_bp || 0) >= 0 ? T.bull : T.bear }}>{r.pnl_bp == null ? '—' : num(r.pnl_bp, 1)}</td>
                <td style={{ padding: '9px 12px' }}><Pill value={r.status} color={STATUS_COLORS[r.status] || T.textMuted} /></td>
                <td style={{ padding: '9px 12px', color: T.textMuted, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
